import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode, ResponseChoiceValue } from 'model/common'
import { AddRankedResponsePayload } from 'model/newApiPayloads'
import { ResponseOptionChoice } from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

const useSendRankedResponse = () => {
  const post = usePostCallbackWithRetry<AddRankedResponsePayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendRankedResponse = async ({
    questionId,
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    questionTypeCode,
    responseChoices
  }: {
    questionId: string
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    questionTypeCode: string
    responseChoices: ResponseOptionChoice[]
  }) => {
    const payload: AddRankedResponsePayload = {
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      question_id: questionId,
      rendered_entry_position: renderedEntryPosition,
      ranked_options: responseChoices.map((choice) => ({
        response_option_id: choice.responseOptionId!,
        rendered_response_option_position:
          choice.renderedPosition ?? choice.position ?? 0,
        rank:
          choice.value === ResponseChoiceValue.NotSelected ||
          choice.value === ResponseChoiceValue.Selected
            ? null
            : parseInt(choice.value as string, 10),
        categorical_choice:
          choice.value === ResponseChoiceValue.NotSelected
            ? ('not_selected' as const)
            : ('selected' as const)
      })),
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(`/surveys/${surveyId}/responses/ranked`)

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Ranked response was sent', {
          questionId,
          questionTypeCode,
          surveyId,
          respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.error(
          'Network Failure: Ranked response failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionId,
            questionTypeCode,
            surveyId,
            respondentId,
            apiUsed: API_DISTRIBUTION_FACET.NEW,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendRankedResponse'
          )
        }
      })

    return promise
  }

  return { sendRankedResponse }
}

export default useSendRankedResponse
